import type { ClassificationStatus, FileIndicators } from '@dataroom/shared';

/** One file's state on the upload panel. Client-side, before the server knows
 * it: `queued` → waiting for an upload slot; `uploading` → its finalize request
 * is in flight; `uploaded` → finalized, not in the polled listing yet. Then the
 * document's own classification status from the listing (`processing`,
 * `classified`, …); `error` → size cap or a 4xx from finalize. */
export type UploadItemStatus = 'queued' | 'uploading' | 'uploaded' | ClassificationStatus | 'error';

/** Where one file's FILING stands, for the panel's summary line (owner
 * direction 2026-10-12: filing is the primary status; OCR and redaction are
 * counted separately). Disjoint:
 *   - `uploading`    queued or being sent;
 *   - `classifying`  uploaded, waiting to be (or being) classified;
 *   - `filed`        filed into a category — done and usable;
 *   - `needs_filing` the AI was not sure: a person picks the category;
 *   - `unfiled`      the AI could not file it at all (ADR-0018 `failed`);
 *   - `paperwork`    deal paperwork (an LOI, an NDA …), never filed by design;
 *   - `failed`       the upload itself failed. */
export type UploadStage =
  | 'uploading'
  | 'classifying'
  | 'filed'
  | 'needs_filing'
  | 'unfiled'
  | 'paperwork'
  | 'failed';

/** The filing stage of one file from its upload / classification status. */
export function uploadStage(status: UploadItemStatus): UploadStage {
  switch (status) {
    case 'queued':
    case 'uploading':
      return 'uploading';
    case 'error':
      return 'failed';
    case 'uploaded':
    case 'processing':
      return 'classifying';
    case 'classified':
      return 'filed';
    case 'low_confidence':
      return 'needs_filing';
    case 'failed':
      return 'unfiled';
    case 'not_classifiable':
      return 'paperwork';
    default:
      return 'classifying';
  }
}

export interface UploadProgress {
  /** e.g. "212 uploaded · 198 filed · 9 need filing · OCR 14 pending · Redaction 61 pending /
   * 137 complete". Empty for no files. */
  line: string;
  /** Files whose filing is settled (filed, needs filing, couldn't file, paperwork, failed) … */
  settled: number;
  /** … of this many (every file but the skipped duplicates). */
  total: number;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The panel's visible summary line and progress: filing first, then the OCR
 * and redaction indicators (`indicators` = each counted file's, null for none). */
export function uploadProgress(
  stages: UploadStage[],
  indicators: (FileIndicators | null)[] = [],
  skipped = 0,
): UploadProgress {
  const n = (s: UploadStage) => stages.filter((x) => x === s).length;
  const ocr = (s: FileIndicators['ocr']) => indicators.filter((x) => x?.ocr === s).length;
  const red = (s: FileIndicators['redaction']) =>
    indicators.filter((x) => x?.redaction === s).length;
  /** "OCR 14 pending / 3 complete" — the non-zero counts of one indicator, or nothing. */
  const group = (name: string, counts: [number, string][]) => {
    const shown = counts.filter(([c]) => c > 0).map(([c, w]) => `${c} ${w}`);
    if (shown.length > 0) parts.push(`${name} ${shown.join(' / ')}`);
  };
  const total = stages.length;
  if (total === 0 && skipped === 0) return { line: '', settled: 0, total: 0 };
  const uploaded = total - n('uploading') - n('failed');
  const parts = [`${uploaded} uploaded`];
  const add = (count: number, words: string) => {
    if (count > 0) parts.push(words);
  };
  add(n('filed'), `${n('filed')} filed`);
  add(n('needs_filing'), plural(n('needs_filing'), 'needs filing', 'need filing'));
  add(n('unfiled'), `${n('unfiled')} couldn't file`);
  add(n('paperwork'), `${n('paperwork')} deal paperwork`);
  add(n('classifying'), `${n('classifying')} classifying`);
  add(n('uploading'), `${n('uploading')} uploading`);
  add(n('failed'), `${n('failed')} failed to upload`);
  group('OCR', [
    [ocr('pending'), 'pending'],
    [ocr('complete'), 'complete'],
    [ocr('failed'), 'failed'],
  ]);
  group('Redaction', [
    [red('pending'), 'pending'],
    [red('complete'), 'complete'],
    [red('needs_review'), red('needs_review') === 1 ? 'needs review' : 'need review'],
    [red('failed'), 'failed'],
  ]);
  add(skipped, `${skipped} already in the room`);
  return {
    line: parts.join(' · '),
    settled: total - n('uploading') - n('classifying'),
    total,
  };
}

/** The words the upload live region announces for the current batch. */
export function uploadSummary(statuses: UploadItemStatus[]): string {
  if (statuses.length === 0) return '';
  const count = (pred: (s: UploadItemStatus) => boolean) => statuses.filter(pred).length;
  const pending = count(
    (s) => s === 'queued' || s === 'uploading' || s === 'uploaded' || s === 'processing',
  );
  const filed = count((s) => s === 'classified');
  const check = count((s) => s === 'low_confidence' || s === 'failed');
  const loi = count((s) => s === 'not_classifiable');
  const failed = count((s) => s === 'error');
  const n = statuses.length;
  const parts = [`${filed} of ${n} ${n === 1 ? 'file' : 'files'} filed`];
  if (pending > 0) parts.push(`${pending} still processing`);
  if (check > 0) parts.push(`${check} to check`);
  if (loi > 0) parts.push(`${loi} deal paperwork, not filed`);
  if (failed > 0) parts.push(`${failed} failed to upload`);
  return parts.join(', ') + '.';
}

// ---------------------------------------------------------------------------
// The skip review (owner ask 2026-10-15: "I'm seeing A LOT of skipped 'already
// in the data room' documents … review the ones that were skipped vs. the ones
// the system says they were identical to"). A skip means the room already held
// a document with exactly these bytes (SHA-256, ADR-0028). The server keeps no
// name for the skipped file (N11) — only this browser knows it — so the review
// is built here, from the picked file and the room's own document listing.
// ---------------------------------------------------------------------------

/** One skipped upload, as the panel lists it and the skip report writes it. */
export interface SkippedUpload {
  /** The picked file's name. */
  name: string;
  /** The folder it was picked or dropped from ("Real Estate/Leases"), or null for a loose file. */
  folder: string | null;
  /** The picked file's size in bytes. */
  size: number | null;
  /** The answer carried no document id: the identical document is one this
   * caller cannot see (the admin hid it from them) — "already in the room", no more. */
  hidden: boolean;
  /** The identical document from the room's listing, when the listing has it. */
  existing: {
    id: string;
    name: string;
    categoryPath: string[];
    createdAt: string;
    folderPath?: string | null;
  } | null;
}

/** The folder part of a picked file's relative path ("Real Estate/Leases/a.pdf"
 * → "Real Estate/Leases"), or null for a loose file. */
export function folderOfRelativePath(relativePath: string | undefined): string | null {
  if (!relativePath) return null;
  const cut = relativePath.lastIndexOf('/');
  return cut > 0 ? relativePath.slice(0, cut) : null;
}

/**
 * A file name reduced to what a person would call "the same name": no folder,
 * no extension, no download-copy marker ("Lease (1)", "Lease - Copy"), case,
 * spaces and punctuation ignored. "PnL_2023 (1).PDF" and "pnl 2023.pdf" match;
 * "PnL 2023" and "PnL 2024" do not.
 */
export function normalizedFileName(name: string): string {
  let stem = (name.split(/[\\/]/).pop() ?? name).replace(/\.[a-z0-9]{1,5}$/i, '').trim();
  for (let i = 0; i < 2; i++) {
    stem = stem
      .replace(/\s*\(\d+\)$/, '')
      .replace(/(?:\s+|\s*-\s*)copy$/i, '')
      .trim();
  }
  return stem.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

/** True when the two names are the same after {@link normalizedFileName}. */
export function sameFileName(a: string, b: string): boolean {
  return normalizedFileName(a) === normalizedFileName(b);
}

/** The skip has a known identical document whose name differs from the
 * uploaded file's — the ones worth a second look. Unknown targets never are. */
export function isDifferentName(skip: SkippedUpload): boolean {
  return skip.existing !== null && !sameFileName(skip.name, skip.existing.name);
}

/** The report's wording for an identical document the caller cannot see, or one
 * the room listing does not (yet) carry. */
export const HIDDEN_TARGET_LABEL = "a document already in the room that you can't see";
export const UNLISTED_TARGET_LABEL = 'a document already in the room (not in the document list)';

const SKIP_REPORT_HEADER = [
  'Uploaded file name',
  'Uploaded from folder',
  'Size (bytes)',
  'Identical to (existing document)',
  'Existing filing path',
  'Existing uploaded from folder',
  'Existing upload date',
  'Same name',
];

/**
 * One CSV cell (RFC 4180): quoted when it holds a comma, quote or line break,
 * with quotes doubled. A cell a spreadsheet would run as a formula (it starts
 * with = + - @ or a tab/return) gets a leading apostrophe — the names come
 * from other people's files.
 */
export function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (typeof value === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The skip report for a batch: a header row, then one row per skipped file
 * (CRLF line ends, RFC 4180). Built entirely from what this browser holds. */
export function skipReportCsv(skips: readonly SkippedUpload[]): string {
  const rows = skips.map((s) => {
    const e = s.existing;
    return [
      s.name,
      s.folder ?? '',
      s.size,
      e ? e.name : s.hidden ? HIDDEN_TARGET_LABEL : UNLISTED_TARGET_LABEL,
      e ? e.categoryPath.join(' / ') : '',
      e?.folderPath ?? '',
      e ? e.createdAt : '',
      e ? (sameFileName(s.name, e.name) ? 'yes' : 'no') : 'unknown',
    ];
  });
  return [SKIP_REPORT_HEADER, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
